import React, { useState, useEffect } from 'react';
import { Package, CheckCircle, AlertTriangle, Clock, Info } from 'lucide-react';
import { isDueFor } from '../../../../services/equipmentMaintenanceService';
import { useVessel } from '../../../../contexts/VesselContext';
// FT-407: Equipment Register migrated to IndexedDB — read via the IDB-backed hook.
// localStorage.getItem() returned [] post-migration, so linked equipment never showed.
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import {
  getEquipmentStatus,
  sortByPriority,
  getStatusColorClass,
  formatDaysUntilDue
} from '../../../../utils/equipmentStatus';

/**
 * Equipment Unit Selector Component (Flat Structure)
 *
 * Shows a list of equipment items linked to this maintenance job,
 * allowing the user to select which items were serviced.
 *
 * Updated Oct 28, 2025 for flat equipment structure (no families/units).
 */
const EquipmentUnitSelector = ({ job, selectedUnits, onSelectionChange }) => {
  const [linkedEquipment, setLinkedEquipment] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const { currentVesselId } = useVessel();
  // FT-407: IDB-backed register. `equipment` is the non-archived combined register,
  // reactive to vessel switch and register writes.
  const { equipment: equipmentList } = useEquipmentRegister();

  // Load linked equipment
  useEffect(() => {
    if (!currentVesselId || !job) {
      setLinkedEquipment([]);
      return;
    }

    // Filter equipment linked to this job. The hook already excludes archived items,
    // so we only need the linked-id membership test here.
    const linkedIds = job.linkedEquipmentIds || [];
    const linked = equipmentList.filter(eq =>
      linkedIds.includes(eq.id) && !eq.archived
    );

    // Calculate service status for each item using new helper
    const enriched = linked.map(item => {
      const statusInfo = getEquipmentStatus(
        item,
        job, // Pass entire job object for service type resolution
        job.interval || 12,
        30 // warning days
      );

      return {
        ...item,
        status: statusInfo.status,
        statusInfo // Include full status info for display
      };
    });

    // Sort by priority (overdue first)
    const sorted = sortByPriority(enriched);
    setLinkedEquipment(sorted);

    // AUTO-SELECT overdue items when modal opens
    const overdueIds = sorted
      .filter(item => item.status === 'overdue' || item.status === 'never')
      .map(item => item.id);

    if (overdueIds.length > 0 && selectedUnits.length === 0) {
      onSelectionChange(overdueIds);
      setSelectAll(overdueIds.length === sorted.length);
    }
    // FT-407: `equipmentList` added to deps — the IDB register loads asynchronously,
    // so the effect must re-run when the hook's data arrives (was [job, currentVesselId]).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, currentVesselId, equipmentList]);

  // Handle select all toggle
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked && linkedEquipment.length > 0) {
      const allItemIds = linkedEquipment.map(item => item.id);
      onSelectionChange(allItemIds);
    } else {
      onSelectionChange([]);
    }
  };

  // Handle select overdue/due soon items
  const handleSelectOverdue = () => {
    const overdueIds = linkedEquipment
      .filter(item => item.status === 'overdue' || item.status === 'dueSoon' || item.status === 'never')
      .map(item => item.id);

    onSelectionChange(overdueIds);
    setSelectAll(overdueIds.length === linkedEquipment.length);
  };

  // Handle individual item selection
  const handleItemToggle = (itemId) => {
    const newSelection = selectedUnits.includes(itemId)
      ? selectedUnits.filter(id => id !== itemId)
      : [...selectedUnits, itemId];

    onSelectionChange(newSelection);
    setSelectAll(newSelection.length === linkedEquipment.length);
  };

  // Don't show if no equipment linked
  if (!linkedEquipment || linkedEquipment.length === 0) {
    return (
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">No equipment linked</p>
            <p className="text-sm text-gray-600 mt-1">
              Add equipment items to Equipment Register and they'll automatically link to this maintenance job.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Get status icon and color
  const getStatusIcon = (status) => {
    switch (status) {
      case 'overdue':
        return { Icon: AlertTriangle, color: 'text-red-600' };
      case 'dueSoon':
        return { Icon: Clock, color: 'text-yellow-600' };
      case 'never':
        return { Icon: Package, color: 'text-gray-400' };
      default:
        return { Icon: CheckCircle, color: 'text-green-600' };
    }
  };

  const formatServiceStatus = (status) => {
    const formats = {
      overdue: { text: 'Overdue', color: 'red' },
      dueSoon: { text: 'Due Soon', color: 'yellow' },
      scheduled: { text: 'Scheduled', color: 'green' },
      never: { text: 'Never Serviced', color: 'gray' }
    };
    return formats[status] || formats.scheduled;
  };

  // Calculate status summary
  const overdue = linkedEquipment.filter(item => item.status === 'overdue').length;
  const dueSoon = linkedEquipment.filter(item => item.status === 'dueSoon').length;

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="px-4 py-3 bg-cyan-50 border-b border-cyan-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Package className="h-5 w-5 text-cyan-600 mr-2" />
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                Equipment Items ({linkedEquipment.length})
              </h4>
              <p className="text-xs text-gray-600 mt-0.5">
                Select items that were serviced
              </p>
            </div>
          </div>

          {/* Status Summary */}
          <div className="flex items-center gap-3">
            {overdue > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {overdue} overdue
              </span>
            )}
            {dueSoon > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                {dueSoon} due soon
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Select All */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">
              Select all items ({linkedEquipment.length})
            </span>
          </label>

          {/* Select Overdue button - only show if there are overdue items */}
          {(overdue > 0 || dueSoon > 0) && (
            <button
              type="button"
              onClick={handleSelectOverdue}
              className="text-xs font-medium text-cyan-600 hover:text-cyan-800 transition-colors"
            >
              Select due items ({overdue + dueSoon})
            </button>
          )}
        </div>
      </div>

      {/* Item List */}
      <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
        {linkedEquipment.map((item) => {
          const { Icon: StatusIcon, color } = getStatusIcon(item.status);
          const statusInfo = formatServiceStatus(item.status);
          const isSelected = selectedUnits.includes(item.id);

          // Get last service date from statusInfo (already filtered by service type)
          const lastService = item.statusInfo?.lastService || null;

          return (
            <label
              key={item.id}
              className={`flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                isSelected ? 'bg-cyan-50' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleItemToggle(item.id)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />

              <div className="ml-3 flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {item.name || item.type}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {item.location && `${item.location} • `}
                      {item.serialNumber && `S/N: ${item.serialNumber}`}
                    </p>
                  </div>

                  <div className="flex flex-col items-end ml-3">
                    <div className="flex items-center">
                      <StatusIcon className={`h-4 w-4 ${color} mr-1`} />
                      <span className={`text-xs font-medium ${color}`}>
                        {statusInfo.text}
                      </span>
                    </div>
                    {/* Show days until due */}
                    {item.statusInfo && item.statusInfo.daysUntilDue !== null && (
                      <span className={`text-xs ${color} mt-0.5`}>
                        {formatDaysUntilDue(item.statusInfo.daysUntilDue)}
                      </span>
                    )}
                  </div>
                </div>

                {item.statusInfo && item.statusInfo.lastService && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last service: {new Date(item.statusInfo.lastService).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {/* Selection Summary Footer */}
      {selectedUnits.length > 0 && (
        <div className="px-4 py-3 bg-green-50 border-t border-green-100">
          <p className="text-sm font-medium text-green-800">
            ✓ {selectedUnits.length} {selectedUnits.length === 1 ? 'item' : 'items'} selected for service
          </p>
        </div>
      )}
    </div>
  );
};

export default EquipmentUnitSelector;
